// Графики дашборда на Chart.js: цвета берутся из CSS-переменных темы .dash.

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  ChartConfiguration,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { RefObject, useEffect, useRef } from 'react';
import { ax, rub } from './fmt';

Chart.register(BarController, BarElement, CategoryScale, LinearScale, LineController, LineElement, PointElement, Tooltip);

export interface Tokens {
  ink: string; muted: string; line: string; surface: string; accent: string; berry: string; good: string; bad: string;
  c: string[];
}

export function tokens(el: HTMLElement | null): Tokens {
  const cs = getComputedStyle(el ?? document.documentElement);
  const g = (n: string) => cs.getPropertyValue(n).trim();
  return {
    ink: g('--ink'), muted: g('--muted'), line: g('--line'), surface: g('--surface'), accent: g('--accent'),
    berry: g('--berry'), good: g('--good'), bad: g('--bad'),
    c: [g('--c1'), g('--c2'), g('--c3'), g('--c4'), g('--c5'), g('--c6'), g('--c7')],
  };
}

type Fmt = (v: number, i: number) => string;
declare module 'chart.js' {
  // подпись значения в подсказке: своя у набора данных
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ChartDatasetProperties<TType, TData> {
    fmt?: Fmt;
  }
}

export function baseOptions(T: Tokens, o: { stack?: boolean; autoSkip?: boolean; yfmt?: (v: number) => string } = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 250 },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: T.ink, titleColor: T.surface, bodyColor: T.surface, padding: 10, cornerRadius: 6,
        callbacks: {
          label: (c: { dataset: { label?: string; fmt?: Fmt }; raw: unknown; dataIndex: number }) =>
            ` ${c.dataset.label}: ${c.dataset.fmt ? c.dataset.fmt(c.raw as number, c.dataIndex) : rub(c.raw as number)}`,
        },
      },
    },
    scales: {
      x: { stacked: !!o.stack, grid: { display: false }, ticks: { color: T.muted, autoSkip: o.autoSkip ?? false, maxRotation: 0, minRotation: 0 } },
      y: { stacked: !!o.stack, grid: { color: T.line }, border: { display: false }, ticks: { color: T.muted, callback: (v: number | string) => (o.yfmt ?? ax)(+v) } },
    },
  };
}

/** График на canvas: пересоздаётся при смене deps, уничтожается при размонтировании. */
export function useChart(make: (T: Tokens) => ChartConfiguration | null, deps: unknown[]): RefObject<HTMLCanvasElement | null> {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    if (!ref.current) return;
    // переменные темы наследуются от .dash: читаем их у самого canvas
    const cfg = make(tokens(ref.current));
    if (!cfg) return;
    Chart.defaults.font.family = getComputedStyle(ref.current).fontFamily;
    Chart.defaults.font.size = 11;
    const chart = new Chart(ref.current, cfg);
    return () => chart.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}

/** Мини-график в карточке показателя. */
export function drawSpark(cv: HTMLCanvasElement | null, vals: number[], color: string) {
  if (!cv) return;
  const dpr = window.devicePixelRatio || 1;
  const w = cv.clientWidth || 160;
  const h = cv.clientHeight || 30;
  cv.width = w * dpr;
  cv.height = h * dpr;
  const x = cv.getContext('2d');
  if (!x) return;
  x.scale(dpr, dpr);
  const v = vals.filter((a) => a != null && isFinite(a));
  if (v.length < 2) return;
  const mn = Math.min(...v);
  const r = Math.max(...v) - mn || 1;
  x.beginPath();
  vals.forEach((a, i) => {
    const px = (i / (vals.length - 1)) * (w - 4) + 2;
    const py = h - 3 - ((a - mn) / r) * (h - 6);
    if (i) x.lineTo(px, py);
    else x.moveTo(px, py);
  });
  x.strokeStyle = color;
  x.lineWidth = 1.6;
  x.lineJoin = 'round';
  x.stroke();
  const ly = h - 3 - ((vals[vals.length - 1] - mn) / r) * (h - 6);
  x.beginPath();
  x.arc(w - 2, ly, 2.6, 0, 7);
  x.fillStyle = color;
  x.fill();
}
